import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
/**
 * Reusable Pagination
 *
 * Props:
 * - total: number (required)
 * - currentPage: number (required)
 * - itemsPerPage: number (required)
 * - onPageChange: (page: number) => void (required)
 * - onPageSizeChange?: (size: number) => void
 * - currentPageCount?: number
 * - pageSizeOptions?: number[]           // default [10, 20, 50, 100]
 * - pageWindowRadius?: number            // default 2
 * - pageJump?: number                    // default 5
 * - infoRenderer?: ({start, end, total}) => React.ReactNode
 * - showPageSize?: boolean               // default true
 * - className?: string
 * - loading?: boolean                    // show skeleton when true
 */

export default function Pagination({
  total,
  currentPage,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  currentPageCount,
  pageSizeOptions = [10, 20, 50, 100],
  pageWindowRadius = 2,
  pageJump = 5,
  infoRenderer,
  showPageSize = true,
  className = "",
  loading = false,
}) {
  const { t } = useTranslation();
  function useMediaQuery(query) {
    const getMatch = () =>
      typeof window !== "undefined" && window.matchMedia(query).matches;

    const [matches, setMatches] = React.useState(getMatch);

    React.useEffect(() => {
      const mql = window.matchMedia(query);
      const onChange = (e) => setMatches(e.matches);

      // Modern browsers
      if (mql.addEventListener) mql.addEventListener("change", onChange);
      // Older Safari
      else mql.addListener(onChange);

      setMatches(mql.matches);

      return () => {
        if (mql.removeEventListener)
          mql.removeEventListener("change", onChange);
        else mql.removeListener(onChange);
      };
    }, [query]);

    return matches;
  }
  // ✅ Hook MUST be called before any early returns
  const isMobile = useMediaQuery("(max-width: 767px)");

  if (total == null) return null;

  const totalPages = Math.max(1, Math.ceil((total || 0) / itemsPerPage));
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;

  const start = total === 0 ? 0 : indexOfFirstItem + 1;
  const end =
    total === 0
      ? 0
      : Math.min(
          indexOfFirstItem +
            (typeof currentPageCount === "number"
              ? currentPageCount
              : itemsPerPage),
          total,
        );

  const handlePrev = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNext = () => onPageChange(Math.min(totalPages, currentPage + 1));
  const handleJump = (delta) =>
    onPageChange(Math.min(totalPages, Math.max(1, currentPage + delta)));

  const handleItemsPerPageChange = (e) => {
    const size = Number(e.target.value);
    if (onPageSizeChange) onPageSizeChange(size);

    // Clamp page if size change reduces total pages
    const newTotalPages = Math.max(1, Math.ceil((total || 0) / size));
    if (currentPage > newTotalPages) onPageChange(newTotalPages);
  };

  const getPageWindow = (current, totalPgs) => {
    if (totalPgs <= 7) {
      return Array.from({ length: totalPgs }, (_, i) => i + 1);
    }
    const pages = [1];
    const left = Math.max(2, current - pageWindowRadius);
    const right = Math.min(totalPgs - 1, current + pageWindowRadius);

    if (left > 2) pages.push("prev-ellipsis");
    for (let p = left; p <= right; p++) pages.push(p);
    if (right < totalPgs - 1) pages.push("next-ellipsis");

    pages.push(totalPgs);
    return pages;
  };

  // SKELETON STATE
  if (loading) {
    return (
      <div className={`pagination-container ${className}`} aria-busy="true">
        <div className="pagination-info">
          <span
            className="skeleton skeleton-text"
            style={{ width: 180, height: 16 }}
          />
        </div>
        <div className="pagination-controls">
          <div className="pagination-size">
            <span
              className="skeleton skeleton-text"
              style={{ width: 110, height: 16 }}
            />
            <span
              className="skeleton skeleton-pill"
              style={{ width: 68, height: 34 }}
            />
          </div>
          <span
            className="skeleton skeleton-btn"
            style={{ width: 34, height: 34 }}
          />
          <span
            className="skeleton skeleton-btn"
            style={{ width: 34, height: 34 }}
          />
          <span
            className="skeleton skeleton-btn"
            style={{ width: 34, height: 34 }}
          />
          <span
            className="skeleton skeleton-btn"
            style={{ width: 34, height: 34 }}
          />
          <span
            className="skeleton skeleton-btn"
            style={{ width: 34, height: 34 }}
          />
        </div>
      </div>
    );
  }

  const pages = getPageWindow(currentPage, totalPages);

  return (
    <div className={`pagination-container ${className}`}>
      <div className="pagination-info">
        {infoRenderer ? (
          infoRenderer({ start, end, total })
        ) : (
          <>
            {t("pagination.showing")} <strong>{start}</strong>–
            <strong>{end}</strong> {t("pagination.of")} <strong>{total}</strong>
          </>
        )}
      </div>

      <div className="pagination-controls">
        {showPageSize && (
          <div className="pagination-size">
            {/* ✅ Hide label on mobile by not rendering it */}
            {!isMobile && (
              <label htmlFor="per-page" className="per-page-label">
                {t("pagination.itemsPerPage")}
              </label>
            )}

            <select
              id="per-page"
              className="per-page-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              aria-label={t("pagination.itemsPerPageAria")}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          className="pagination-btn"
          onClick={handlePrev}
          disabled={currentPage === 1}
          aria-label={t("pagination.previousPage")}
        >
          <ChevronLeft />
        </button>

        {pages.map((item, idx) => {
          if (typeof item === "number") {
            return (
              <button
                key={`p-${item}`}
                className={`pagination-btn ${currentPage === item ? "active" : ""}`}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            );
          }

          if (item === "prev-ellipsis") {
            return (
              <button
                key={`pe-${idx}`}
                className="pagination-btn pagination-ellipsis"
                onClick={() => handleJump(-pageJump)}
                aria-label={t("pagination.jumpBackPages", { count: pageJump })}
              >
                …
              </button>
            );
          }

          return (
            <button
              key={`ne-${idx}`}
              className="pagination-btn pagination-ellipsis"
              onClick={() => handleJump(pageJump)}
              aria-label={t("pagination.jumpForwardPages", { count: pageJump })}
            >
              …
            </button>
          );
        })}

        <button
          className="pagination-btn"
          onClick={handleNext}
          disabled={currentPage === totalPages}
          aria-label={t("pagination.nextPage")}
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  );
}
